/**
 * Quota-safe localStorage writes.
 *
 * The app caches large analysis payloads client-side (competitor dossiers,
 * forecast/sentiment results, dataset previews, per-user snapshots). Browsers
 * cap localStorage at ~5-10 MB per origin, so heavy analytics users hit
 * QuotaExceededError and every subsequent write silently fails — state stops
 * persisting (see the "[AuthContext] Failed to persist user" console warning).
 *
 * safeSetItem retries after evicting regenerable caches, biggest first.
 * Everything listed here is either re-fetchable from the backend
 * (analysis-cache, dataset catalog) or best-effort by design (snapshots).
 */

// Tier 1: pure caches — regenerable from the backend (analysis-cache,
// dataset catalog, presentation collections) or recomputable on demand.
const EVICTABLE_EXACT_KEYS = [
  'intelligence360_preview_cache',
  'forecast_result_cache',
  'sentiment_result_cache',
  'intelligence360_result_cache',
  'email_assistant_cache',
  'ai_writer_cache',
  'xpherium_dataset_catalog_cache_v1',
  'sentimentAnalysis_data',
  'kpi_cached_info',
  'forecast_cached_info',
  'saved_presentations_cache',
  'analytics_presentation_insights',
  'kpi_presentation_insights',
  'ci_competitors',
];

const EVICTABLE_PREFIXES = [
  'xph_storage_snapshot_',        // per-user preference snapshots (best-effort)
  'xpherium_kpi_analysis_state_', // KPI result state (backend analysis-cache backs it)
];

// Tier 2 — last resort: module workbench states. These are large (sheets +
// analyses) but every one of them is synced to the backend workbench state
// (see utils/workbenchSync.js), so the next module mount re-hydrates from
// the server. Only evicted when tier 1 didn't free enough space.
const LAST_RESORT_EXACT_KEYS = [
  'bi_workbench_v9',
  'kpi_workbench_v2',
  'sentiment_workbench_v2',
  'intelligence360_workbench_v2',
  'forecast_workbench_v1',
  'dp_workbench_v1',
  'account_intel_workbench_v1',
];

const LAST_RESORT_PREFIXES = [
  'xph_storage_account_intel_workbench_v2_',
];

function listEvictionCandidates(skipKey) {
  const tier1 = [];
  const tier2 = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || key === skipKey) continue;
      const size = (window.localStorage.getItem(key) || '').length;
      if (EVICTABLE_EXACT_KEYS.includes(key) || EVICTABLE_PREFIXES.some((p) => key.startsWith(p))) {
        tier1.push({ key, size });
      } else if (LAST_RESORT_EXACT_KEYS.includes(key) || LAST_RESORT_PREFIXES.some((p) => key.startsWith(p))) {
        tier2.push({ key, size });
      }
    }
  } catch { /* ignore */ }
  // Evict biggest first within each tier — frees the most space per removal.
  const bySize = (a, b) => b.size - a.size;
  return [...tier1.sort(bySize), ...tier2.sort(bySize)];
}

/**
 * Write to localStorage; on quota errors evict regenerable caches and retry.
 * Returns true when the value was persisted, false otherwise (never throws).
 */
export function safeSetItem(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch { /* fall through to eviction */ }

  const candidates = listEvictionCandidates(key);
  for (const { key: evictKey } of candidates) {
    try { window.localStorage.removeItem(evictKey); } catch { /* ignore */ }
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch { /* keep evicting */ }
  }

  try {
    // eslint-disable-next-line no-console
    console.warn('[safeStorage] localStorage still full after evicting caches; dropping write', {
      key,
      valueBytes: typeof value === 'string' ? value.length : null,
    });
  } catch { /* ignore */ }
  return false;
}

export default safeSetItem;
